import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "redux/store";
import {
  setIsLoading,
  setShowToast,
  incrementRequests,
  decrementRequests,
} from "../rfpModuleSlice/application";
import { api } from "api/axiosInterceptor";
import { convertToCamelCase, openNotificationWithIcon } from "utils/utils";
import { JobData } from "modules/hr/types/type";
import { UserData } from "types/rfp";
import { apiUrl } from "api/apiUrl";

interface ResumeAnalysisResponse {
  message: string;
  files: any[];
  resume: any[];
  job_description: any;
}

interface ApplicationData {
  id?: string;
  jobId?: string;
  jobTitle?: string;
  applicationId?: string;
  candidateName?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

interface ApplicationsResponse {
  data: ApplicationData[];
  pagination: {
    current_page: number;
    page_length: number;
    total_pages: number;
    total: number;
  };
}

const initialState = {
  candidateEvaluationData: {} as JobData,
  userList: [] as UserData[],
  roles: [] as string[],
  mailWithRole: [] as { email: string; role: string }[],
  resumeAnalysisData: null as ResumeAnalysisResponse | null,
  applicationsData: [] as ApplicationData[],
  applicationsPagination: {
    current_page: 1,
    page_length: 10,
    total_pages: 0,
    total: 0,
  },
};

const slice = createSlice({
  name: "hrFlow",
  initialState,
  reducers: {
    setCandidateData(state, action: PayloadAction<JobData>) {
      state.candidateEvaluationData = action.payload;
    },
    getHrInviteUserList(
      state,
      action: PayloadAction<{
        users: UserData[];
        role: string[];
      }>
    ) {
      const { users, role } = action.payload;
      state.userList = users;
      state.roles = role;
    },
    setHrStakeholderMails(
      state,
      action: PayloadAction<
        {
          email: string;
          role: string;
        }[]
      >
    ) {
      state.mailWithRole = action.payload;
    },
    setResumeAnalysisData(
      state,
      action: PayloadAction<ResumeAnalysisResponse>
    ) {
      state.resumeAnalysisData = action.payload;
    },
    setApplicationsData(
      state,
      action: PayloadAction<{
        data: ApplicationData[];
        pagination: {
          current_page: number;
          page_length: number;
          total_pages: number;
          total: number;
        };
      }>
    ) {
      state.applicationsData = action.payload.data;
      state.applicationsPagination = action.payload.pagination;
    },
  },
});

export const { setCandidateData, getHrInviteUserList, setHrStakeholderMails, setResumeAnalysisData, setApplicationsData } =
  slice.actions;

export default slice.reducer;

export const uploadHrFile = (files: File[]) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });
      const response = await api.post("/hr/vertical/file-uploads/", formData);
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Failed to upload file. Please try again.",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
};

export const uploadHrResume = (files: File[]) => {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });
      const response = await api.post("/hr/vertical/resume/uploads/", formData);
      
      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Failed to upload resume. Please try again.",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
};

export const getHrFlowData = () => {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const response = await api.get("/hr/vertical/extraction/data/");
      const formattedData = convertToCamelCase(response.data);
      dispatch(setCandidateData(formattedData));

      return response.status;
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Failed to get HR flow data. Please try again.",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
};

export function getHrInviteStakeholders() {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/hr/vertical/user/stakeholder/records/`;
      const { data, status } = await api.get(path);
      if (status === 200) {
        dispatch(getHrInviteUserList(data));
      }
    } catch {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function sendHrInvitation(
  jobId: number,
  data: { email: string; role: string }[]
) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/hr/vertical/user/stakeholder/invite/${jobId}/`;

      const response = await api.post(path, {
        data,
      });
      if (response.status === 200) {
        dispatch(setHrStakeholderMails([]));
      }
      return response.status;
    } catch (err) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Something went wrong!",
        })
      );
      return err;
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function deleteHrInvitee(email: string, jobId: string) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/hr/vertical/user/stakeholder/remove/${jobId}`;
      const mail = {
        email,
      };
      const { status } = await api.delete(path, {
        headers: {
          "Content-Type": "application/json",
        },
        data: mail,
      });

      if (status === 200) {
        openNotificationWithIcon(
          "success",
          "User removed successfully.",
          `User removed successfully from the invitees list.`
        );
      }

      return status;
    } catch (error) {
      openNotificationWithIcon(
        "error",
        "Something went wrong!",
        `Something went wrong while removing the user from the invitee list.`
      );
    } finally {
      dispatch(decrementRequests());
    }
  };
}

export function getHrApplications(pageNumber = 1, pageLength = 10, state: 'Active' | 'Inactive' | 'Completed' = 'Active') {
    return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `${apiUrl.hr}applicatons/`;
      const { data, status } = await api.post<ApplicationsResponse>(path, {
        page_length: pageLength,
        page_number: pageNumber,
        state,
      });
      if (status === 200 || status === 201) {
        const formattedData = convertToCamelCase(data.data || []);
        dispatch(setApplicationsData({
          data: formattedData,
          pagination: data.pagination || {
            current_page: pageNumber,
            page_length: pageLength,
            total_pages: 0,
            total: 0,
          },
        }));
        return { status, data };
      }
      return { status, data: null };
    } catch (error: any) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Failed to fetch applications. Please try again.",
        })
      );
      return { status: error?.response?.status || 500, data: null };
    } finally {
      dispatch(decrementRequests());
    }
  };
}

// Helper function to convert camelCase to snake_case
const toSnakeCase = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

// Helper function to convert object keys from camelCase to snake_case recursively
const convertKeysToSnakeCase = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(convertKeysToSnakeCase);
  }
  
  if (typeof obj === "object") {
    const converted: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const snakeKey = toSnakeCase(key);
        converted[snakeKey] = convertKeysToSnakeCase(obj[key]);
      }
    }
    return converted;
  }
  
  return obj;
};

export function confirmEvaluationCriteria(data: JobData) {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      // Convert camelCase to snake_case
      const apiData = convertKeysToSnakeCase(data);
      
      const path = `/hr/vertical/extraction/data/`;
      const response = await api.post(path, apiData);
      
      if (response.status === 200 || response.status === 201) {
        dispatch(
          setShowToast({
            show: true,
            type: "success",
            toastMessage: "Evaluation criteria confirmed successfully.",
          })
        );
      }
      return response.status;
    } catch (error: any) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Failed to confirm evaluation criteria. Please try again.",
        })
      );
      return error?.response?.status || error;
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function getHrAnalysisData() {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `/hr/vertical/analysis/data/`;
      const response = await api.get(path);
      
      if (response.data) {
        dispatch(setResumeAnalysisData(response.data));
      }
      
      return response.status;
    } catch (error: any) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Failed to fetch analysis data. Please try again.",
        })
      );
      return error?.response?.status || error;
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

function getFileNameFromContentDisposition(response: any): string {
  const disposition = response.headers["content-disposition"];
  const contentType = response.headers["content-type"];
  let filename = "download";

  if (disposition) {
    try {
      const starMatch = disposition.match(/filename\*\s*=\s*([^;]+)/i);
      if (starMatch && starMatch[1]) {
        const parts = starMatch[1].split("''");
        if (parts.length === 2) {
          filename = decodeURIComponent(parts[1]);
        }
      } else {
        const nameMatch = disposition.match(/filename\s*=\s*["']?([^"';]+)["']?/i);
        if (nameMatch && nameMatch[1]) {
          filename = nameMatch[1];
        }
      }
    } catch (err) {
      console.warn("Error extracting filename from Content-Disposition:", err);
    }
  }

  if (!/\.[a-z0-9]+$/i.test(filename)) {
    const extMap: Record<string, string> = {
      "application/pdf": ".pdf",
      "application/msword": ".doc",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
      "application/vnd.ms-excel": ".xls",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
    };
    const ext = extMap[contentType];
    if (ext) {
      filename += ext;
    }
  }

  return filename;
}

export function downloadHrFile(file_id: string) {
  return async (dispatch: AppDispatch) => {
    dispatch(setIsLoading(true));
    try {
      const path = `/hr/vertical/files/download/${file_id}/`;
      const response = await api.get(path, { responseType: "blob" });
      if (response.status === 200 || response.status === 201) {
        const contentType = response.headers["content-type"];
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          getFileNameFromContentDisposition(response)
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        dispatch(
          setShowToast({
            show: true,
            type: "success",
            toastMessage: "File downloaded successfully!",
          })
        );
      }
    } catch (error) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: "Failed to download file. Please try again.",
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };
}

export function rejectCandidate(candidateId: string | number) {
  return async (dispatch: AppDispatch) => {
    dispatch(incrementRequests());
    try {
      const path = `/hr/vertical/application/candidate/reject/${String(candidateId)}/`;
      const response = await api.get(path);
      if (response.status === 200 || response.status === 201) {
        dispatch(
          setShowToast({
            show: true,
            type: "success",
            toastMessage: "Candidate rejected successfully!",
          })
        );
        dispatch(getHrAnalysisData());
        return response.status;
      }
    } catch (error: any) {
      dispatch(
        setShowToast({
          show: true,
          type: "error",
          toastMessage: error?.response?.data?.detail || "Failed to reject candidate. Please try again.",
        })
      );
      return null;
    } finally {
      dispatch(decrementRequests());
    }
  };
}






